import { useSuspenseQuery } from "@tanstack/react-query";
import { addMonths, differenceInMonths } from "date-fns";
import { useEffect, useState } from "react";

import { Star01Icon } from "@/components/Icons/Star01Icon";
import { NoData } from "@/components/NoData/NoData";
import { Stat } from "@/components/Stat/Stat";
import { Inline } from "@/design-system/Inline";
import { Tiles } from "@/design-system/Tiles";
import { isDefined } from "@/helpers/util";
import { AnalyticsCard } from "@/modules/analytics/components/AnalyticsCard";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { useTranslation } from "@/translations";

const RANGE = "lastYear";
export function TicketOverviewCard(): React.ReactNode {
  const { t } = useTranslation();
  const analyticsQueries = useAnalyticsQueries();
  const [minDate] = useState(() => addMonths(new Date(), -12));
  const [maxDate] = useState(() => addMonths(new Date(), -1));
  const [date, setDate] = useState(maxDate);
  const [monthIndex, setMonthIndex] = useState(0);
  const { data: ticketStats } = useSuspenseQuery(analyticsQueries.ticketOverviewMonthly(RANGE));
  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setMonthIndex(differenceInMonths(maxDate, date));
  }, [date, maxDate]);
  const currentData = ticketStats.data[monthIndex];
  const currentBenchmark = ticketStats.benchmark[monthIndex];
  const noData = ticketStats.data.every((x) => x.averageTicketRating === undefined || x.ticketCount === 0);

  return (
    <AnalyticsCard.Root>
      <AnalyticsCard.Header
        title={t("page.analytics-overview.chart.tickets-overview.title")}
        tooltip={t("page.analytics-overview.chart.tickets-overview.title-tooltip")}
      >
        <AnalyticsCard.FilterPeriod type="month" value={date} onChange={setDate} min={minDate} max={maxDate} />
      </AnalyticsCard.Header>
      {noData ? (
        <NoData />
      ) : (
        <Tiles columns={{ base: 1, container: { sm: 2 } }} gap="sm">
          <Stat
            label={t("page.analytics-overview.chart.tickets-overview.item.total-tickets")}
            value={currentData.ticketCount}
          />
          <Stat
            label={t("page.analytics-overview.chart.tickets-overview.item.average-rating")}
            value={currentData.averageTicketRating}
            renderValue={(value) => {
              if (!isDefined(value)) {
                return <span>-</span>;
              }

              return (
                <Inline gap="sm" align="center">
                  <Star01Icon size={24} tone="rating" />
                  <span>
                    {currentData.averageTicketRating}
                    <span className="text-heading-2">/10</span>
                  </span>
                </Inline>
              );
            }}
            renderFooter={() => {
              if (!currentBenchmark?.averageTicketRating) {
                return undefined;
              }

              return `${currentBenchmark.averageTicketRating}/10 ${t("page.analytics-overview.benchmark")}`;
            }}
          />
          <Stat
            label={t("page.analytics-overview.chart.tickets-overview.item.closed-tickets")}
            value={currentData.ticketClosedCount}
          />
          <Stat
            label={t("page.analytics-overview.chart.tickets-overview.item.closed-by-resident")}
            value={Math.round(currentData.ticketClosedByResidentPercentage)}
            renderValue={(value) => (isDefined(value) ? `${value}%` : "-")}
            renderFooter={() =>
              currentBenchmark
                ? `${Math.round(currentBenchmark.ticketClosedByResidentPercentage)}% ${t(
                    "page.analytics-overview.benchmark",
                  )}`
                : undefined
            }
          />
        </Tiles>
      )}
    </AnalyticsCard.Root>
  );
}
